import React, { useCallback, useEffect, useRef, useState } from "react";
import { clamp, cloneDeep } from "lodash";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  websiteBuilderMoveContainer,
  websiteBuilderMoveElement,
  websiteBuilderSwapGridCellContent,
  websiteBuilderUpdateSelectedNode,
} from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { SECTION_ENTITY_VIEW_INDICATOR_HEIGHT, SECTION_ENTITY_VIEW_INDICATOR_OFFSET, SectionEntityViewIndicator, TSectionEntityViewIndicator } from "../helpers/SectionEntityViewIndicator";
import { IWebsiteBuilderHeader, WEBSITE_BUILDER_HEADER_ID } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderFooter, WEBSITE_BUILDER_FOOTER_ID } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { TWebsiteBuilderSection, TWebsiteBuilderSectionLayout, websiteBuilderGenerateSectionBounds, websiteBuilderGetSectionPosition, websiteBuilderGetSectionVisibility } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { useWebsiteBuilderEditorContext } from "pages/secure/facility/websiteBuilder/editor/websiteBuilderEditorContext";
import { IWebsiteBuilderSectionEntityMoveMouseOffset } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSectionEntity";
import { IWebsiteBuilderNodeDimensions, IWebsiteBuilderNodePosition } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNodeGeometry";
import { isWebsiteBuilderContainerViewContext, isWebsiteBuilderElementViewContext, isWebsiteBuilderSectionEntityInSectionContext, TWebsiteBuilderSectionEntityViewContext } from "../helpers/sectionEntityViewContext";
import { isWebsiteBuilderGridCell, IWebsiteBuilderGridCell, websiteBuilderGridCellFilled } from "pages/secure/facility/websiteBuilder/editor/nodes/gridCell/websiteBuilderGridCell";
import { isWebsiteBuilderGrid, IWebsiteBuilderGrid } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderGrid";
import { TWebsiteBuilderContainer } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderContainer";
import { isWebsiteBuilderTabset, IWebsiteBuilderTabset } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTabset";
import { websiteBuilderCalculateSectionEntityInsertLayout, websiteBuilderGetFreeformParentNextZIndex } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderFreeformParentNode";
import { isWebsiteBuilderElementContainerParentNode, isWebsiteBuilderElementFlowParentNode, isWebsiteBuilderElementFreeformParentNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNode";
import { IWebsiteBuilderRoot, WEBSITE_BUILDER_ROOT_ID } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import { isWebsiteBuilderTab, IWebsiteBuilderTab, websiteBuilderGenerateTabDimensions } from "pages/secure/facility/websiteBuilder/editor/nodes/tab/websiteBuilderTab";

export interface IUseSectionEntityViewMoveArgs {
  context: TWebsiteBuilderSectionEntityViewContext;
  moving: boolean;
  filled: boolean;
}

type TSectionEntityViewMoveRefScope = "section" | "container";

interface ISectionEntityViewMoveRefScopedPosition {
  scope: TSectionEntityViewMoveRefScope;
  position: IWebsiteBuilderNodePosition;
}

interface ISectionEntityViewMoveRefState {
  nextScopedPosition: ISectionEntityViewMoveRefScopedPosition;
  nextDimensions: IWebsiteBuilderNodeDimensions;
  mouseOffset: IWebsiteBuilderSectionEntityMoveMouseOffset;
  moving: boolean;
}

interface ISectionEntityViewMoveState {
  allowed: boolean;
  indicatorPosition: IWebsiteBuilderNodePosition;
  movePosition: IWebsiteBuilderNodePosition;
  moveDimensions: IWebsiteBuilderNodeDimensions;
  westBoundary: number;
  eastBoundary: number;
  topBoundary: number;
  bottomBoundary: number;
}

type TSectionEntityHandleViewMovePointerDown = (e: React.PointerEvent<HTMLDivElement>) => void;

interface IUseSectionEntityViewMove {
  viewMovePosition: IWebsiteBuilderNodePosition;
  viewMoveDimensions: IWebsiteBuilderNodeDimensions;
  viewMoveIndicator: JSX.Element;
  handleViewMovePointerDown: TSectionEntityHandleViewMovePointerDown;
}

export function useSectionEntityViewMove(args: IUseSectionEntityViewMoveArgs): IUseSectionEntityViewMove {
  const dispatch = useAppDispatch();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const activePageNodeMap = useAppSelector(store => store.websiteBuilderStore.editor.activePageNodeMap);
  
  const selectedNode = useAppSelector(store => store.websiteBuilderStore.editor.selectedNode);
  
  const sectionMoveTargetId =
    selectedNode?.editingMode?.type === "element-move" || selectedNode?.editingMode?.type === "container-move" ?
      selectedNode.editingMode.sectionMoveTargetId : null;

  const containerMoveTargetId = selectedNode?.editingMode?.type === "element-move" ? selectedNode.editingMode.containerMoveTargetId : null;
  
  const gridCellMoveTargetId = selectedNode?.editingMode?.type === "element-move" ? selectedNode.editingMode.gridCellMoveTargetId : null;

  const tabMoveTargetId = selectedNode?.editingMode?.type === "element-move" ? selectedNode.editingMode.tabMoveTargetId : null;

  const { activePageDomNodeRefMap, overlayLinesRef, designSpaceRef, designSpaceBoundaries } = useWebsiteBuilderEditorContext();

  const contextStretch = isWebsiteBuilderSectionEntityInSectionContext(args.context)
    ? args.context.stretch
    : null;

  const contextSectionNode: TWebsiteBuilderSection =
    isWebsiteBuilderSectionEntityInSectionContext(args.context) ? args.context.parentNode : args.context.sectionNode;
  
  const contextContainerNodeAncestor: TWebsiteBuilderContainer =
    args.context.type === "element-in-container" ? args.context.parentNode :
      args.context.type === "element-in-grid-cell" ? args.context.gridNode :
        args.context.type === "element-in-tab" ? args.context.tabsetNode : null;
  
  const contextGridCellNodeAncestor: IWebsiteBuilderGridCell = args.context.type === "element-in-grid-cell" ? args.context.parentNode : null;
  
  const contextTabNodeAncestor: IWebsiteBuilderTab = args.context.type === "element-in-tab" ? args.context.parentNode : null;

  const moveRefState = useRef<ISectionEntityViewMoveRefState>({
    nextScopedPosition: null,
    nextDimensions: null,
    mouseOffset: null,
    moving: false,
  });

  const [moveState, setMoveState] = useState<ISectionEntityViewMoveState>({
    allowed: false,
    indicatorPosition: null,
    movePosition: null,
    moveDimensions: null,
    westBoundary: null,
    eastBoundary: null,
    topBoundary: null,
    bottomBoundary: null,
  });

  const indicator: TSectionEntityViewIndicator = 
    args.moving ?
    {
      type: "position",
      position: moveState.indicatorPosition,
      showOnTop:
        moveState.topBoundary < (moveState.indicatorPosition?.y ?? 0) - SECTION_ENTITY_VIEW_INDICATOR_HEIGHT - SECTION_ENTITY_VIEW_INDICATOR_OFFSET,
    } :
    null;

  const viewMovePosition: IWebsiteBuilderNodePosition =
    args.moving && moveState.movePosition ?
      moveState.movePosition :
      (isWebsiteBuilderSectionEntityInSectionContext(args.context) ?
        args.context.activeScreenLayoutData.position :
        args.context.activeLayoutData.position
      );

  const viewMoveDimensions: IWebsiteBuilderNodeDimensions =
    args.moving && moveState.moveDimensions ?
      moveState.moveDimensions :
      (isWebsiteBuilderSectionEntityInSectionContext(args.context) ?
        args.context.activeScreenLayoutData.dimensions :
        args.context.activeLayoutData.dimensions
      );

  const handleCalculateMovePosition = useCallback((
    e: PointerEvent,
    mouseOffset: IWebsiteBuilderSectionEntityMoveMouseOffset,
    rect: DOMRect,
    moveScopeRect: DOMRect,
    westBoundary: number,
    eastBoundary: number,
    topBoundary: number,
    bottomBoundary: number,
  ): IWebsiteBuilderNodePosition => {
    const position: IWebsiteBuilderNodePosition = {
      x: e.clientX - mouseOffset.x - moveScopeRect.x,
      y: e.clientY - mouseOffset.y - moveScopeRect.y,
    };

    if (overlayLinesRef.current.horizontal != null) {
      const { anchor, y1 } = overlayLinesRef.current.horizontal;

      switch (anchor) {
        case "top": {
          position.y = y1 - moveScopeRect.y;
          break;
        }
        case "center": {
          position.y = y1 - rect.height / 2 - moveScopeRect.y;
          break;
        }
        case "bottom": {
          position.y = y1 - rect.height - moveScopeRect.y;
          break;
        }
      }
    }
    if (overlayLinesRef.current.vertical != null) {
      const { anchor, x1 } = overlayLinesRef.current.vertical;
    
      switch (anchor) {
        case "left": {
          position.x = x1 - moveScopeRect.x;
          break;
        }
        case "center": {
          position.x = x1 - rect.width / 2 - moveScopeRect.x;
          break;
        }
        case "right": {
          position.x = x1 - rect.width - moveScopeRect.x;
          break;
        }
      }
    }

    position.x = clamp(position.x, westBoundary, eastBoundary);
    position.y = clamp(position.y, topBoundary, bottomBoundary);

    return position;
  }, []);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const { nextScopedPosition, mouseOffset, moving } = moveRefState.current;

      const ref = activePageDomNodeRefMap.current.get(args.context.node.id);
      const boundingRect = ref?.getBoundingClientRect();

      if (
        boundingRect == null ||
        nextScopedPosition == null ||
        mouseOffset == null
      ) {
        return;
      }

      if (isWebsiteBuilderSectionEntityInSectionContext(args.context)) {
        const sectionRef = activePageDomNodeRefMap.current.get(contextSectionNode.id);
        const sectionBoundingRect = sectionRef?.getBoundingClientRect();

        if (sectionBoundingRect == null) {
          return;
        }

        const nextScreenPosition = handleCalculateMovePosition(
          e,
          mouseOffset,
          boundingRect,
          sectionBoundingRect,
          moveState.westBoundary,
          moveState.eastBoundary,
          moveState.topBoundary,
          moveState.bottomBoundary,
        );

        const nextPosition = websiteBuilderGetSectionPosition(designSpaceBoundaries.leftX, nextScreenPosition);

        if (contextStretch?.enabled) {
          nextScreenPosition.x = contextStretch.margin;
          nextPosition.x = 0;
        }

        moveRefState.current.nextScopedPosition = {
          scope: nextScopedPosition.scope,
          position: nextPosition,
        };

        const updatedIndicatorPosition = contextStretch?.enabled ? nextScreenPosition : nextPosition;

        setMoveState(prev => ({
          ...prev,
          indicatorPosition: { ...updatedIndicatorPosition },
          movePosition: { ...nextScreenPosition },
        }));

        if (!moving) {
          moveRefState.current.moving = true;

          if (args.context.node.type === "element") {
            dispatch(
              websiteBuilderUpdateSelectedNode({
                selectedNodeUpdates: {
                  type: "element",
                  editingMode: {
                    type: "element-move",
                    mouseOffset: { ...mouseOffset },
                    designSpaceLeftXBoundary: designSpaceBoundaries.leftX,
                    designSpaceRightXBoundary: designSpaceBoundaries.rightX,
                    sectionMoveTargetId: null,
                    containerMoveTargetId: null,
                    gridCellMoveTargetId: null,
                    tabMoveTargetId: null,
                  },
                },
              }),
            );
          } else if (args.context.node.type === "container") {
            dispatch(
              websiteBuilderUpdateSelectedNode({
                selectedNodeUpdates: {
                  type: "container",
                  editingMode: {
                    type: "container-move",
                    mouseOffset: { ...mouseOffset },
                    designSpaceLeftXBoundary: designSpaceBoundaries.leftX,
                    designSpaceRightXBoundary: designSpaceBoundaries.rightX,
                    sectionMoveTargetId: null,
                  },
                },
              }),
            );
          }
        }
      } else if (
        args.context.type === "element-in-container" ||
        args.context.type === "element-in-grid-cell" ||
        args.context.type === "element-in-tab"
      ) {
        const containerRef = activePageDomNodeRefMap.current.get(contextContainerNodeAncestor.id);
        const containerBoundingRect = containerRef?.getBoundingClientRect();

        if (containerBoundingRect == null) {
          return;
        }

        const nextPosition = handleCalculateMovePosition(
          e,
          mouseOffset,
          boundingRect,
          containerBoundingRect,
          moveState.westBoundary,
          moveState.eastBoundary,
          moveState.topBoundary,
          moveState.bottomBoundary,
        );

        moveRefState.current.nextScopedPosition = {
          scope: nextScopedPosition.scope,
          position: nextPosition,
        };

        setMoveState(prev => ({
          ...prev,
          indicatorPosition: { ...nextPosition },
          movePosition: { ...nextPosition },
        }));

        if (!moving) {
          moveRefState.current.moving = true;

          dispatch(
            websiteBuilderUpdateSelectedNode({
              selectedNodeUpdates: {
                type: "element",
                editingMode: {
                  type: "element-move",
                  mouseOffset: { ...mouseOffset },
                  designSpaceLeftXBoundary: designSpaceBoundaries.leftX,
                  designSpaceRightXBoundary: designSpaceBoundaries.rightX,
                  sectionMoveTargetId: null,
                  containerMoveTargetId: null,
                  gridCellMoveTargetId: null,
                  tabMoveTargetId: null,
                },
              },
            }),
          );
        }
      }
    },
    [
      args.context,
      contextSectionNode.id,
      contextContainerNodeAncestor?.id,
      contextStretch,
      designSpaceBoundaries.leftX,
      designSpaceBoundaries.rightX,
      moveState.westBoundary,
      moveState.eastBoundary,
      moveState.topBoundary,
      moveState.bottomBoundary,
      handleCalculateMovePosition,
    ],
  );

  const handleCleanupMove = useCallback((e: PointerEvent) => {
    const ref = activePageDomNodeRefMap.current.get(args.context.node.id);

    ref?.releasePointerCapture(e.pointerId);

    moveRefState.current = {
      nextScopedPosition: null,
      nextDimensions: null,
      mouseOffset: null,
      moving: false,
    };

    setMoveState(prev => ({
      ...prev,
      allowed: false,
      indicatorPosition: null,
      movePosition: null,
      moveDimensions: null,
      westBoundary: null,
      eastBoundary: null,
      topBoundary: null,
      bottomBoundary: null,
    }));
  }, [args.context.node.id]);

  const handleAbortMove = useCallback((e: PointerEvent) => {
    dispatch(
      websiteBuilderUpdateSelectedNode({
        selectedNodeUpdates: {
          type: args.context.node.type === "element" ? "element" : "container",
          editingMode: {
            type: "none",
          },
        },
      }),
    );

    handleCleanupMove(e);
  }, [args.context.node.type, handleCleanupMove]);

  const handleMove = useCallback((
    e: PointerEvent,
    prevIndex: number,
    nextIndex: number,
    nextZIndex: number,
    nextSectionId: string,
    nextContainerId: string,
    nextGridCellId: string,
    nextTabId: string,
    nextSectionLayout: TWebsiteBuilderSectionLayout,
    nextPosition: IWebsiteBuilderNodePosition,
  ) => {
    const { nextDimensions } = moveRefState.current;

    if (nextDimensions == null) {
      handleAbortMove(e);
      return;
    }

    const nextSectionDesktopBounds = websiteBuilderGenerateSectionBounds(nextSectionLayout, "desktop");
    const nextSectionTabletBounds = websiteBuilderGenerateSectionBounds(nextSectionLayout, "tablet");
    const nextSectionMobileBounds = websiteBuilderGenerateSectionBounds(nextSectionLayout, "mobile");

    const prevSectionId = contextSectionNode.id;

    const newSectionDestination = prevSectionId !== nextSectionId;
    const oldSectionDestination = prevSectionId === nextSectionId;

    const prevLayout = cloneDeep(args.context.layout);
    let nextLayout = cloneDeep(prevLayout);

    nextLayout[deviceView].dimensions = nextDimensions;

    const applyStretchOverride = () => {
      if (!contextStretch?.enabled) {
        return;
      }

      nextLayout.desktop.position.x = 0;
      nextLayout.desktop.dimensions.width = nextSectionDesktopBounds.width;
      nextLayout.tablet.position.x = 0;
      nextLayout.tablet.dimensions.width = nextSectionTabletBounds.width;
      nextLayout.mobile.position.x = 0;
      nextLayout.mobile.dimensions.width = nextSectionMobileBounds.width;
    };
    
    if (isWebsiteBuilderElementViewContext(args.context)) {
      const prevContainerId = contextContainerNodeAncestor?.id == null ? null : contextContainerNodeAncestor.id;
      const prevGridCellId = contextGridCellNodeAncestor?.id == null ? null : contextGridCellNodeAncestor.id;
      const prevTabId = contextTabNodeAncestor?.id == null ? null : contextTabNodeAncestor.id;

      const newNonSectionDestination = 
        (nextContainerId != null && nextContainerId !== prevContainerId) ||
        (nextGridCellId != null && nextGridCellId !== prevGridCellId) ||
        (nextTabId != null && nextTabId !== prevTabId);

      const outOfOldNonSectionLocation =
        (prevContainerId != null && nextContainerId == null) ||
        (prevGridCellId != null && nextGridCellId == null) ||
        (prevTabId != null && nextTabId == null);

      if (newNonSectionDestination) {
        const nextContainerNode = nextContainerId == null ? null : activePageNodeMap.get(nextContainerId);

        const getOriginPosition = (): IWebsiteBuilderNodePosition => ({ x: 0, y: 0 });

        if (nextTabId != null) {
          if (
            !isWebsiteBuilderTabset(nextContainerNode) ||
            !nextContainerNode.children.some(tabNode => tabNode.id === nextTabId)
          ) {
            handleAbortMove(e);
            return;
          }

          nextLayout = websiteBuilderCalculateSectionEntityInsertLayout(
            nextPosition,
            nextLayout,
            deviceView,
            websiteBuilderGenerateTabDimensions(nextContainerNode, "desktop"),
            websiteBuilderGenerateTabDimensions(nextContainerNode, "tablet"),
            websiteBuilderGenerateTabDimensions(nextContainerNode, "mobile"),
          );
        } else if (nextGridCellId != null) {
          if (
            !isWebsiteBuilderGrid(nextContainerNode) ||
            !nextContainerNode.children.some(gridCellNode => gridCellNode.id === nextGridCellId)
          ) {
            handleAbortMove(e);
            return;
          }

          nextLayout.desktop.position = getOriginPosition();
          nextLayout.tablet.position = getOriginPosition();
          nextLayout.mobile.position = getOriginPosition();
        } else if (nextContainerId != null) {
          if (!isWebsiteBuilderElementContainerParentNode(nextContainerNode)) {
            handleAbortMove(e);
            return;
          }

          if (isWebsiteBuilderElementFreeformParentNode(nextContainerNode)) {
            nextLayout = websiteBuilderCalculateSectionEntityInsertLayout(
              nextPosition,
              nextLayout,
              deviceView,
              nextContainerNode.container.layout.desktop.dimensions,
              nextContainerNode.container.layout.tablet.dimensions,
              nextContainerNode.container.layout.mobile.dimensions,
            );
          } else {
            nextLayout.desktop.position = getOriginPosition();
            nextLayout.tablet.position = getOriginPosition();
            nextLayout.mobile.position = getOriginPosition();
          }
        } else {
          handleAbortMove(e);
          return;
        }
      } else if (
        newSectionDestination ||
        (oldSectionDestination && outOfOldNonSectionLocation)
      ) {
        nextLayout = websiteBuilderCalculateSectionEntityInsertLayout(
          nextPosition,
          nextLayout,
          deviceView,
          nextSectionDesktopBounds,
          nextSectionTabletBounds,
          nextSectionMobileBounds,
        );
      } else {
        nextLayout[deviceView].position = nextPosition;
      }

      applyStretchOverride();

      dispatch(
        websiteBuilderMoveElement(activePageId, deviceView, args.context.node.id, {
          id: args.context.node.id,
          prevIndex,
          nextIndex,
          prevZIndex: args.context.zIndex,
          nextZIndex,
          prevSectionId,
          nextSectionId,
          prevContainerId,
          nextContainerId,
          prevGridCellId,
          nextGridCellId,
          prevTabId,
          nextTabId,
          prevLayout,
          nextLayout,
        }),
      );
    } else if (isWebsiteBuilderContainerViewContext(args.context)) {
      if (newSectionDestination) {
        nextLayout = websiteBuilderCalculateSectionEntityInsertLayout(
          nextPosition,
          nextLayout,
          deviceView,
          nextSectionDesktopBounds,
          nextSectionTabletBounds,
          nextSectionMobileBounds,
        );
      } else {
        nextLayout[deviceView].position = nextPosition;
      }

      applyStretchOverride();

      dispatch(
        websiteBuilderMoveContainer(activePageId, deviceView, args.context.node.id, {
          id: args.context.node.id,
          prevIndex,
          nextIndex,
          prevZIndex: args.context.zIndex,
          nextZIndex,
          prevSectionId,
          nextSectionId,
          prevLayout,
          nextLayout,
        }),
      );
    } else {
      handleAbortMove(e);
      return;
    }

    handleCleanupMove(e);
  }, [
    contextSectionNode.id,
    args.context,
    contextContainerNodeAncestor?.id,
    contextGridCellNodeAncestor?.id,
    contextTabNodeAncestor?.id,
    contextStretch,
    deviceView,
    activePageId,
    activePageNodeMap,
    handleAbortMove,
    handleCleanupMove,
  ]);

  const handleMoveElementToTab = useCallback((e: PointerEvent, prevIndex: number) => {
    const tabMoveTarget = activePageNodeMap.get(tabMoveTargetId);

    if (!isWebsiteBuilderTab(tabMoveTarget)) {
      handleAbortMove(e);
      return;
    }

    const header = activePageNodeMap.get(WEBSITE_BUILDER_HEADER_ID) as IWebsiteBuilderHeader;
    const root = activePageNodeMap.get(WEBSITE_BUILDER_ROOT_ID) as IWebsiteBuilderRoot;
    const footer = activePageNodeMap.get(WEBSITE_BUILDER_FOOTER_ID) as IWebsiteBuilderFooter;

    const sectionNodes: TWebsiteBuilderSection[] = [header, ...root.children, footer];

    let tabMoveTargetParentNode: IWebsiteBuilderTabset = null;
    let tabMoveTargetGrandParentNode: TWebsiteBuilderSection = null;

    for (let i = 0; i < sectionNodes.length && tabMoveTargetParentNode == null && tabMoveTargetGrandParentNode == null; i++) {
      const sectionNode = sectionNodes[i];

      for (let j = 0; j < sectionNode.children.length && tabMoveTargetParentNode == null && tabMoveTargetGrandParentNode == null; j++) {
        const sectionEntityNode = sectionNode.children[j];

        if (isWebsiteBuilderTabset(sectionEntityNode) && sectionEntityNode.children.some(tabNode => tabNode.id === tabMoveTargetId)) {
          tabMoveTargetParentNode = sectionEntityNode;
          tabMoveTargetGrandParentNode = sectionNode;
        }
      }
    }

    if (tabMoveTargetParentNode == null || tabMoveTargetGrandParentNode == null) {
      handleAbortMove(e);
      return;
    }

    const { nextScopedPosition } = moveRefState.current;

    const ref = activePageDomNodeRefMap.current.get(args.context.node.id);
    const sectionRef = activePageDomNodeRefMap.current.get(contextSectionNode.id);
    const tabMoveTargetRef = activePageDomNodeRefMap.current.get(tabMoveTargetId);

    const boundingRect = ref?.getBoundingClientRect();
    const sectionBoundingRect = sectionRef?.getBoundingClientRect();
    const tabMoveTargetBoundingRect = tabMoveTargetRef?.getBoundingClientRect();

    if (
      nextScopedPosition == null ||
      nextScopedPosition.position == null ||
      boundingRect == null ||
      sectionBoundingRect == null ||
      tabMoveTargetBoundingRect == null
    ) {
      handleAbortMove(e);
      return;
    }

    const containerRef = activePageDomNodeRefMap.current.get(contextContainerNodeAncestor?.id);
    const containerBoundingRect = containerRef?.getBoundingClientRect();

    if (nextScopedPosition.scope === "container" && containerBoundingRect == null) {
      handleAbortMove(e);
      return;
    }

    const getTabPosition = (): IWebsiteBuilderNodePosition => {
      if (nextScopedPosition.scope === "container") {
        return {
          x: clamp(containerBoundingRect.x - tabMoveTargetBoundingRect.x + nextScopedPosition.position.x, 0, tabMoveTargetBoundingRect.width - boundingRect.width),
          y: clamp(containerBoundingRect.y - tabMoveTargetBoundingRect.y + nextScopedPosition.position.y, 0, tabMoveTargetBoundingRect.height - boundingRect.height),
        };
      } else if (nextScopedPosition.scope === "section") {
        return {
          x: clamp(sectionBoundingRect.x + designSpaceBoundaries.leftX - tabMoveTargetBoundingRect.x + nextScopedPosition.position.x, 0, tabMoveTargetBoundingRect.width - boundingRect.width),
          y: clamp(sectionBoundingRect.y - tabMoveTargetBoundingRect.y + nextScopedPosition.position.y, 0, tabMoveTargetBoundingRect.height - boundingRect.height),
        };
      } else {
        return null;
      }
    };

    const nextPosition = getTabPosition();

    if (nextPosition == null) {
      handleAbortMove(e);
      return;
    }

    if (contextTabNodeAncestor?.id !== tabMoveTargetId) {
      const nextZIndex = websiteBuilderGetFreeformParentNextZIndex(tabMoveTarget);

      handleMove(
        e,
        prevIndex,
        tabMoveTarget.children.length,
        nextZIndex,
        tabMoveTargetGrandParentNode.id,
        tabMoveTargetParentNode.id,
        null,
        tabMoveTarget.id,
        tabMoveTargetGrandParentNode.section.layout,
        nextPosition,
      );
    } else if (
      Math.abs(args.context.activeLayoutData.position.x - nextPosition.x) > 1 ||
      Math.abs(args.context.activeLayoutData.position.y - nextPosition.y) > 1
    ) {
      handleMove(
        e,
        prevIndex,
        prevIndex,
        args.context.zIndex,
        contextSectionNode.id,
        contextContainerNodeAncestor.id,
        null,
        contextTabNodeAncestor.id,
        contextSectionNode.section.layout,
        nextPosition,
      );
    } else {
      handleAbortMove(e);
    }
  }, [
    activePageNodeMap,
    tabMoveTargetId,
    args.context,
    contextSectionNode,
    contextContainerNodeAncestor?.id,
    contextTabNodeAncestor?.id,
    designSpaceBoundaries.leftX,
    handleAbortMove,
    handleMove,
  ]);

  const handleMoveElementToGridCell = useCallback((e: PointerEvent, prevIndex: number) => {
    const gridCellMoveTarget = activePageNodeMap.get(gridCellMoveTargetId);

    if (
      !isWebsiteBuilderGridCell(gridCellMoveTarget) ||
      (args.context.type === "element-in-grid-cell" && gridCellMoveTargetId === args.context.parentNode.id)
    ) {
      handleAbortMove(e);
      return;
    }

    const nextZIndex = 0;

    const nextPosition: IWebsiteBuilderNodePosition = {
      x: 0,
      y: 0,
    };

    const header = activePageNodeMap.get(WEBSITE_BUILDER_HEADER_ID) as IWebsiteBuilderHeader;
    const root = activePageNodeMap.get(WEBSITE_BUILDER_ROOT_ID) as IWebsiteBuilderRoot;
    const footer = activePageNodeMap.get(WEBSITE_BUILDER_FOOTER_ID) as IWebsiteBuilderFooter;
    
    const sectionNodes: TWebsiteBuilderSection[] = [header, ...root.children, footer];

    let gridCellMoveTargetParentNode: IWebsiteBuilderGrid = null;
    let gridCellMoveTargetGrandParentNode: TWebsiteBuilderSection = null;
    
    for (let i = 0; i < sectionNodes.length && gridCellMoveTargetParentNode == null && gridCellMoveTargetGrandParentNode == null; i++) {
      const sectionNode = sectionNodes[i];

      for (let j = 0; j < sectionNode.children.length && gridCellMoveTargetParentNode == null && gridCellMoveTargetGrandParentNode == null; j++) {
        const sectionEntityNode = sectionNode.children[j];

        if (isWebsiteBuilderGrid(sectionEntityNode) && sectionEntityNode.children.some(child => child.id === gridCellMoveTargetId)) {
          gridCellMoveTargetParentNode = sectionEntityNode;
          gridCellMoveTargetGrandParentNode = sectionNode;
        }
      }
    }

    if (gridCellMoveTargetParentNode == null || gridCellMoveTargetGrandParentNode == null) {
      handleAbortMove(e);
      return;
    }

    if (args.context.type === "element-in-grid-cell" && gridCellMoveTargetParentNode.id === args.context.gridNode.id) {
      dispatch(
        websiteBuilderSwapGridCellContent(activePageId, deviceView, args.context.node.id, {
          sectionId: args.context.sectionNode.id,
          gridId: args.context.gridNode.id,
          elementId: args.context.node.id,
          prevGridCellId: args.context.parentNode.id,
          nextGridCellId: gridCellMoveTargetId,
        }),
      );

      handleCleanupMove(e);
    } else if (!websiteBuilderGridCellFilled(gridCellMoveTarget)) {
      handleMove(
        e,
        prevIndex,
        0,
        nextZIndex,
        gridCellMoveTargetGrandParentNode.id,
        gridCellMoveTargetParentNode.id,
        gridCellMoveTargetId,
        null,
        gridCellMoveTargetGrandParentNode.section.layout,
        nextPosition,
      );
    } else {
      handleAbortMove(e);
    }
  }, [
    activePageNodeMap,
    gridCellMoveTargetId,
    args.context,
    handleAbortMove,
    activePageId,
    deviceView,
    handleCleanupMove,
    handleMove,
  ]);

  const handleMoveElementToContainer = useCallback((e: PointerEvent, prevIndex: number) => {
    const containerMoveTarget = activePageNodeMap.get(containerMoveTargetId);

    if (
      !isWebsiteBuilderElementContainerParentNode(containerMoveTarget) ||
      !containerMoveTarget.container.visibility[deviceView] ||
      (
        isWebsiteBuilderElementFlowParentNode(containerMoveTarget) &&
        args.context.type === "element-in-container" &&
        containerMoveTargetId === args.context.parentNode.id
      )
    ) {
      handleAbortMove(e);
      return;
    }

    const header = activePageNodeMap.get(WEBSITE_BUILDER_HEADER_ID) as IWebsiteBuilderHeader;
    const root = activePageNodeMap.get(WEBSITE_BUILDER_ROOT_ID) as IWebsiteBuilderRoot;
    const footer = activePageNodeMap.get(WEBSITE_BUILDER_FOOTER_ID) as IWebsiteBuilderFooter;

    const sectionNodes: TWebsiteBuilderSection[] = [header, ...root.children, footer];

    const containerMoveTargetParentNode = sectionNodes
      .find(sectionNode => sectionNode.children.some(child => child.id === containerMoveTargetId));
  
    if (containerMoveTargetParentNode == null || !websiteBuilderGetSectionVisibility(containerMoveTargetParentNode, root)[deviceView]) {
      handleAbortMove(e);
      return;
    }

    if (isWebsiteBuilderElementFlowParentNode(containerMoveTarget)) {
      const nextZIndex = 0;

      const nextPosition: IWebsiteBuilderNodePosition = {
        x: 0,
        y: 0,
      };

      handleMove(
        e,
        prevIndex,
        containerMoveTarget.children.length,
        nextZIndex,
        containerMoveTargetParentNode.id,
        containerMoveTarget.id,
        null,
        null,
        containerMoveTargetParentNode.section.layout,
        nextPosition,
      );
    } else if (isWebsiteBuilderElementFreeformParentNode(containerMoveTarget)) {
      const { nextScopedPosition } = moveRefState.current;

      const ref = activePageDomNodeRefMap.current.get(args.context.node.id);
      const sectionRef = activePageDomNodeRefMap.current.get(contextSectionNode.id);
      const containerMoveTargetRef = activePageDomNodeRefMap.current.get(containerMoveTargetId);
      
      const boundingRect = ref?.getBoundingClientRect();
      const sectionBoundingRect = sectionRef?.getBoundingClientRect();
      const containerMoveTargetBoundingRect = containerMoveTargetRef?.getBoundingClientRect();

      if (
        nextScopedPosition == null ||
        nextScopedPosition.position == null ||
        boundingRect == null ||
        sectionBoundingRect == null ||
        containerMoveTargetBoundingRect == null
      ) {
        handleAbortMove(e);
        return;
      }

      const containerRef = activePageDomNodeRefMap.current.get(contextContainerNodeAncestor?.id);
      const containerBoundingRect = containerRef?.getBoundingClientRect();

      if (nextScopedPosition.scope === "container" && containerBoundingRect == null) {
        handleAbortMove(e);
        return;
      }

      const getContainerPosition = (): IWebsiteBuilderNodePosition => {
        if (nextScopedPosition.scope === "container") {
          return {
            x: clamp(containerBoundingRect.x - containerMoveTargetBoundingRect.x + nextScopedPosition.position.x, 0, containerMoveTargetBoundingRect.width - boundingRect.width),
            y: clamp(containerBoundingRect.y - containerMoveTargetBoundingRect.y + nextScopedPosition.position.y, 0, containerMoveTargetBoundingRect.height - boundingRect.height),
          };
        } else if (nextScopedPosition.scope === "section") {
          return {
            x: clamp(sectionBoundingRect.x + designSpaceBoundaries.leftX - containerMoveTargetBoundingRect.x + nextScopedPosition.position.x, 0, containerMoveTargetBoundingRect.width - boundingRect.width),
            y: clamp(sectionBoundingRect.y - containerMoveTargetBoundingRect.y + nextScopedPosition.position.y, 0, containerMoveTargetBoundingRect.height - boundingRect.height),
          };
        } else {
          return null;
        }
      };

      const nextPosition = getContainerPosition();

      if (nextPosition == null) {
        handleAbortMove(e);
        return;
      }

      if (contextContainerNodeAncestor?.id !== containerMoveTargetId) {
        const nextZIndex = websiteBuilderGetFreeformParentNextZIndex(containerMoveTarget);

        handleMove(
          e,
          prevIndex,
          containerMoveTarget.children.length,
          nextZIndex,
          containerMoveTargetParentNode.id,
          containerMoveTarget.id,
          null,
          null,
          containerMoveTargetParentNode.section.layout,
          nextPosition,
        );
      } else if (
        Math.abs(args.context.activeLayoutData.position.x - nextPosition.x) > 1 ||
        Math.abs(args.context.activeLayoutData.position.y - nextPosition.y) > 1
      ) {
        handleMove(
          e,
          prevIndex,
          prevIndex,
          args.context.zIndex,
          contextSectionNode.id,
          contextContainerNodeAncestor.id,
          null,
          null,
          contextSectionNode.section.layout,
          nextPosition,
        );
      } else {
        handleAbortMove(e);
      }
    }
  }, [
    activePageNodeMap,
    containerMoveTargetId,
    deviceView,
    args.context,
    contextSectionNode,
    contextContainerNodeAncestor?.id,
    designSpaceBoundaries.leftX,
    handleAbortMove,
    handleMove,
  ]);

  const handleMoveSectionEntityToSection = useCallback((
    e: PointerEvent,
    prevIndex: number,
  ) => {
    const { nextScopedPosition } = moveRefState.current;

    const ref = activePageDomNodeRefMap.current.get(args.context.node.id);
    const sectionRef = activePageDomNodeRefMap.current.get(contextSectionNode.id);
    
    const boundingRect = ref?.getBoundingClientRect();
    const sectionBoundingRect = sectionRef?.getBoundingClientRect();

    if (
      nextScopedPosition == null ||
      nextScopedPosition.position == null ||
      boundingRect == null ||
      sectionBoundingRect == null
    ) {
      handleAbortMove(e);
      return;
    }

    const containerRef = activePageDomNodeRefMap.current.get(contextContainerNodeAncestor?.id);
    const containerBoundingRect = containerRef?.getBoundingClientRect();

    if (nextScopedPosition.scope === "container" && containerBoundingRect == null) {
      handleAbortMove(e);
      return;
    }

    const sectionMoveTarget = activePageNodeMap.get(sectionMoveTargetId);
    const root = activePageNodeMap.get(WEBSITE_BUILDER_ROOT_ID);

    const sectionMoveTargetRef = activePageDomNodeRefMap.current.get(sectionMoveTargetId);
    const sectionMoveTargetRect = sectionMoveTargetRef?.getBoundingClientRect();

    if (
      sectionMoveTarget == null ||
      sectionMoveTargetRect == null ||
      sectionMoveTarget.type !== "section" ||
      root?.type !== "root" ||
      !websiteBuilderGetSectionVisibility(sectionMoveTarget, root)[deviceView] ||
      sectionMoveTargetRect.height < boundingRect.height
    ) {
      handleAbortMove(e);
      return;
    }

    const getSectionPosition = (): IWebsiteBuilderNodePosition => {
      let position: IWebsiteBuilderNodePosition = null;

      if (nextScopedPosition.scope === "container") {
        position = websiteBuilderGetSectionPosition(designSpaceBoundaries.leftX, {
          x: containerBoundingRect.x - sectionMoveTargetRect.x + nextScopedPosition.position.x,
          y: containerBoundingRect.y - sectionMoveTargetRect.y + nextScopedPosition.position.y,
        });
      } else if (nextScopedPosition.scope === "section") {
        position = {
          x: sectionBoundingRect.x - sectionMoveTargetRect.x + nextScopedPosition.position.x,
          y: sectionBoundingRect.y - sectionMoveTargetRect.y + nextScopedPosition.position.y,
        };
      } else {
        return null;
      }

      return {
        x: clamp(position.x, 0, designSpaceBoundaries.width - boundingRect.width),
        y: clamp(position.y, 0, sectionMoveTargetRect.height - boundingRect.height),
      };
    };

    const nextPosition = getSectionPosition();

    if (nextPosition == null) {
      handleAbortMove(e);
      return;
    }

    if (contextSectionNode.id === sectionMoveTargetId) {
      if (
        args.context.type === "element-in-container" ||
        args.context.type === "element-in-grid-cell" ||
        args.context.type === "element-in-tab"
      ) {
        const nextIndex = contextSectionNode.children.length;
        const nextZIndex = websiteBuilderGetFreeformParentNextZIndex(contextSectionNode);
        
        handleMove(
          e,
          prevIndex,
          nextIndex,
          nextZIndex,
          contextSectionNode.id,
          null,
          null,
          null,
          contextSectionNode.section.layout,
          nextPosition,
        );
      } else if (
        isWebsiteBuilderSectionEntityInSectionContext(args.context) &&
        (
          Math.abs(args.context.activeLayoutData.position.x - nextPosition.x) > 1 ||
          Math.abs(args.context.activeLayoutData.position.y - nextPosition.y) > 1
        )
      ) {
        handleMove(
          e,
          prevIndex,
          prevIndex,
          args.context.zIndex,
          contextSectionNode.id,
          null,
          null,
          null,
          contextSectionNode.section.layout,
          nextPosition,
        );
      } else {
        handleAbortMove(e);
      }
    } else {
      const nextIndex = sectionMoveTarget.children.length;
      const nextZIndex = websiteBuilderGetFreeformParentNextZIndex(sectionMoveTarget);

      handleMove(
        e,
        prevIndex,
        nextIndex,
        nextZIndex,
        sectionMoveTarget.id,
        null,
        null,
        null,
        sectionMoveTarget.section.layout,
        nextPosition,
      );
    }
  }, [
    args.context,
    contextSectionNode,
    handleAbortMove,
    contextContainerNodeAncestor?.id,
    designSpaceBoundaries.width,
    sectionMoveTargetId,
    deviceView,
    handleMove,
    activePageNodeMap,
  ]);

  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      const { moving } = moveRefState.current;

      if (!moving) {
        handleCleanupMove(e);
        return;
      }

      const prevIndex = args.context.type !== "element-in-grid-cell" ?
        args.context.parentNode.children.findIndex(child => child.id === args.context.node.id) :
        websiteBuilderGridCellFilled(args.context.parentNode) && args.context.parentNode.children[0].id === args.context.node.id ? 0 : -1;

      if (prevIndex === -1) {
        handleAbortMove(e);
        return;
      }

      if (isWebsiteBuilderElementViewContext(args.context) && tabMoveTargetId != null) {
        handleMoveElementToTab(e, prevIndex);
      } else if (isWebsiteBuilderElementViewContext(args.context) && gridCellMoveTargetId != null) {
        handleMoveElementToGridCell(e, prevIndex);
      } else if (isWebsiteBuilderElementViewContext(args.context) && containerMoveTargetId != null) {
        handleMoveElementToContainer(e, prevIndex);
      } else if (sectionMoveTargetId != null) {
        handleMoveSectionEntityToSection(e, prevIndex);
      } else {
        handleAbortMove(e);
      }
    },
    [
      handleCleanupMove,
      args.context,
      handleAbortMove,
      tabMoveTargetId,
      handleMoveElementToTab,
      gridCellMoveTargetId,
      handleMoveElementToGridCell,
      containerMoveTargetId,
      handleMoveElementToContainer,
      sectionMoveTargetId,
      handleMoveSectionEntityToSection,
    ],
  );

  useEffect(() => {
    if (moveState.allowed) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    }

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [moveState.allowed, handlePointerMove, handlePointerUp]);

  const handleViewMovePointerDown: TSectionEntityHandleViewMovePointerDown = e => {
    const ref = activePageDomNodeRefMap.current.get(args.context.node.id);
    const sectionRef = activePageDomNodeRefMap.current.get(contextSectionNode.id);
    
    const boundingRect = ref?.getBoundingClientRect();
    const sectionBoundingRect = sectionRef?.getBoundingClientRect();
    const designSpaceBoundingRect = designSpaceRef.current?.getBoundingClientRect();

    if (
      boundingRect == null ||
      sectionBoundingRect == null ||
      designSpaceBoundingRect == null
    ) {
      return;
    }
    
    let westBoundary: number = null;
    let eastBoundary: number = null;
    let topBoundary: number = null;
    let bottomBoundary: number = null;

    let scope: TSectionEntityViewMoveRefScope = null;

    if (isWebsiteBuilderSectionEntityInSectionContext(args.context)) {
      westBoundary = designSpaceBoundaries.leftX;
      eastBoundary = designSpaceBoundaries.rightX - boundingRect.width;
      topBoundary = designSpaceBoundingRect.top - sectionBoundingRect.top;
      bottomBoundary = designSpaceBoundingRect.bottom - sectionBoundingRect.top - boundingRect.height;
      
      scope = "section";
    } else if (
      args.context.type === "element-in-container" ||
      args.context.type === "element-in-grid-cell" ||
      args.context.type === "element-in-tab"
    ) {
      const containerRef = activePageDomNodeRefMap.current.get(contextContainerNodeAncestor.id);
      const containerBoundingRect = containerRef?.getBoundingClientRect();

      if (containerBoundingRect == null) {
        return;
      }

      westBoundary = sectionBoundingRect.x + designSpaceBoundaries.leftX - containerBoundingRect.x;
      eastBoundary = sectionBoundingRect.x + designSpaceBoundaries.rightX - boundingRect.width - containerBoundingRect.x;
      topBoundary = designSpaceBoundingRect.top - containerBoundingRect.top;
      bottomBoundary = designSpaceBoundingRect.bottom - containerBoundingRect.top - boundingRect.height;
    
      scope = "container";
    }

    if (
      westBoundary == null ||
      eastBoundary == null ||
      topBoundary == null ||
      bottomBoundary == null ||
      scope == null
    ) {
      return;
    }

    ref.setPointerCapture(e.pointerId);

    const moveDimensions: IWebsiteBuilderNodeDimensions = args.filled ?
      {
        width: boundingRect.width,
        height: boundingRect.height,
      } :
      isWebsiteBuilderSectionEntityInSectionContext(args.context) ?
        args.context.activeScreenLayoutData.dimensions :
        args.context.activeLayoutData.dimensions;

    moveRefState.current.nextDimensions = moveDimensions;

    moveRefState.current.mouseOffset = {
      x: e.clientX - boundingRect.x,
      y: e.clientY - boundingRect.y,
    };

    moveRefState.current.nextScopedPosition = {
      scope,
      position: null,
    };

    moveRefState.current.moving = false;

    setMoveState(prev => ({
      ...prev,
      allowed: true,
      moveDimensions,
      westBoundary,
      eastBoundary,
      topBoundary,
      bottomBoundary,
    }));
  };

  return {
    viewMovePosition,
    viewMoveDimensions,
    viewMoveIndicator: <SectionEntityViewIndicator indicator={indicator} />,
    handleViewMovePointerDown,
  };
}
